import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "api/client/axios.client";
import { ApiResponse } from "types/common.types";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SignInCommand {
  phoneNumber: string;
  password: string;
  languageId: number;
}

export interface SignInResult {
  accessToken: string;
  refreshToken: string;
}

// ─── API Call ────────────────────────────────────────────────────────────────

const signIn = async (
  command: SignInCommand
): Promise<ApiResponse<SignInResult>> => {
  const { data } = await apiClient.post<ApiResponse<SignInResult>>(
    "authentication/v1/UserProfile/External/SignIn",
    command
  );
  return data;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useSignIn = () => {
  return useMutation({
    mutationFn: signIn,
  });
};

// ─── Verification Codes Types & API ──────────────────────────────────────────

export interface RequestVerificationCodeDto {
  phoneNumber: string;
  invitationCode: number;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  languageId: number;
}

export interface RequestVerificationCodeAnswereDto {
  isEmailInvitation: boolean;
  invitationType: number;
}

export interface ConfirmRequestCodeDto {
  phoneNumber: string;
  verificationCode: string;
  email?: string;
  companyName?: string;
  registryCode?: string;
}

export interface ResendVerificationCodeDto {
  phoneNumber: string;
}

// API Calls
const requestVerificationCode = async (
  data: RequestVerificationCodeDto
): Promise<ApiResponse<RequestVerificationCodeAnswereDto>> => {
  const res = await apiClient.post(
    "authentication/v1/User/External/RequestVerificationCode",
    data
  );
  return res.data;
};

const confirmVerificationCode = async (
  data: ConfirmRequestCodeDto
): Promise<ApiResponse<string>> => {
  const res = await apiClient.post(
    "authentication/v1/User/External/ConfirmVerificationCode",
    data
  );
  return res.data;
};

const resendVerificationCode = async (
  data: ResendVerificationCodeDto
): Promise<ApiResponse<string>> => {
  const res = await apiClient.post(
    "authentication/v1/User/External/ResendVerificationCode",
    data
  );
  return res.data;
};

// Hooks
export const useRequestCode = () =>
  useMutation({ mutationFn: requestVerificationCode });
export const useConfirmCode = () =>
  useMutation({ mutationFn: confirmVerificationCode });
export const useResendCode = () =>
  useMutation({ mutationFn: resendVerificationCode });

// ─── User Phone Number (External) ────────────────────────────────────────────

// متد فراخوانی مستقیم API (قابل استفاده با async/await)
export const getUserPhoneNumber = async (
  userId: string
): Promise<ApiResponse<string>> => {
  const res = await apiClient.get<ApiResponse<string>>(
    "authentication/v1/UserProfile/External/GetPhoneNumber",
    {
      params: { userId },
    }
  );
  return res.data;
};

// هوک React Query برای استفاده در کامپوننت‌ها
export const useGetUserPhoneNumber = (userId?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["userPhoneNumber", userId],
    queryFn: () => getUserPhoneNumber(userId!),
    enabled: Boolean(userId) && enabled,
  });
};
